import React, { useEffect, useMemo, useRef, useState } from "react";

import { PieChart as PieIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import Skeleton from "@/components/shared/Skeleton";
import { COMPLIANCE_STATUS } from "../../constant";

// Status-distribution donut (Phase 2A, DL-Q2): candidates by compliance status.
// Visual companion to the flat stat cards; slices drill through to a
// pre-filtered Document Intake (DL-Q5). Fed from the existing GET /stats — no
// new endpoint needed. Colors mirror the stat-card accents on the dashboard.
const SLICES = [
  { key: "pendingFromCandidate", status: COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE, label: "Pending from Candidate", color: "#a855f7" },
  { key: "pendingForReview", status: COMPLIANCE_STATUS.PENDING_FOR_REVIEW, label: "Pending for Review", color: "#3b82f6" },
  { key: "reRequested", status: COMPLIANCE_STATUS.RE_REQUESTED, label: "Re-requested", color: "#f59e0b" },
  { key: "approved", status: COMPLIANCE_STATUS.APPROVED, label: "Approved", color: "#22c55e" },
  { key: "rejected", status: COMPLIANCE_STATUS.REJECTED, label: "Rejected", color: "#ef4444" },
];

// Hovering a slice used to open a floating Recharts tooltip, which landed on
// top of the centred total and the legend beside it. The hovered slice is shown
// in the hole instead: it is always in the same place and can never collide
// with anything.
const StatusDonut = ({ stats, loading }) => {
  const navigate = useNavigate();
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const resetTimerRef = useRef(null);

  const data = useMemo(() => SLICES.map((s) => ({ ...s, value: stats?.[s.key] ?? 0 })).filter((s) => s.value > 0), [stats]);

  const total = data.reduce((sum, s) => sum + s.value, 0);

  // Crossing the padding gap between two slices fires leave-then-enter. Without
  // a short grace period the centre flickers back to the total in between.
  const handleSliceEnter = (slice) => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    setHoveredSlice(slice);
  };
  const handleSliceLeave = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => setHoveredSlice(null), 60);
  };

  useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    [],
  );

  const displayCount = hoveredSlice ? hoveredSlice.value : total;
  const displayLabel = hoveredSlice ? hoveredSlice.label : "candidates";
  const displayPct = hoveredSlice && total > 0 ? Math.round((hoveredSlice.value / total) * 100) : null;

  const goToStatus = (status) => status && navigate(`/admin/compliance/intake?status=${status}`);

  return (
    <Card className="p-0 overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-border-primary flex items-center gap-1.5">
        <PieIcon size={14} className="text-text-primary/70" />
        <h3 className="text-sm font-semibold text-text-primary">Status Distribution</h3>
      </div>

      <div className="p-3.5 sm:p-4 flex-1 flex flex-col justify-center overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Skeleton className="h-40 w-40 rounded-full" />
          </div>
        ) : total === 0 ? (
          <p className="text-xs text-text-secondary py-12 text-center">No candidates in the compliance workflow yet.</p>
        ) : (
          <div className="flex flex-col sm:flex-row items-center gap-4 lg:flex-col xl:flex-row">
            {/* Donut with a fixed, hover-aware centre */}
            <div className="relative w-[165px] h-[165px] flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="label"
                    innerRadius={56}
                    outerRadius={78}
                    paddingAngle={2}
                    stroke="none"
                    onClick={(slice) => goToStatus(slice?.status)}
                    onMouseEnter={(slice) => handleSliceEnter(slice)}
                    onMouseLeave={handleSliceLeave}
                    className="cursor-pointer focus:outline-none"
                  >
                    {data.map((s) => (
                      <Cell key={s.key} fill={s.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-1 text-center">
                <span
                  className="text-[1.4rem] font-bold leading-tight text-text-primary transition-colors"
                  style={hoveredSlice ? { color: hoveredSlice.color } : undefined}
                >
                  {displayCount}
                </span>
                <span className="text-[9.5px] font-medium text-text-secondary leading-tight max-w-[85px] line-clamp-2 px-0.5 mt-0.5">{displayLabel}</span>
                {displayPct !== null && <span className="text-[9px] text-text-secondary/70 mt-0.5 leading-none">{displayPct}% of total</span>}
              </div>
            </div>

            {/* Clickable legend */}
            <div className="flex-1 flex flex-col gap-1.5 w-full min-w-0">
              {data.map((s) => (
                <Button
                  key={s.key}
                  type="button"
                  onClick={() => goToStatus(s.status)}
                  onMouseEnter={() => handleSliceEnter(s)}
                  onMouseLeave={handleSliceLeave}
                  className="flex items-center gap-2 text-left hover:bg-background-tertiary rounded-md px-1.5 py-1 transition-colors"
                >
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                  <span className="text-[11px] text-text-primary flex-1 truncate">{s.label}</span>
                  <span className="text-[11px] font-semibold text-text-secondary">{s.value}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default StatusDonut;
